import { Box, Container, Grid, IconButton, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { completeNewPassword, submitPasswordReset } from '@nutrien/cxp-auth-module';
import {
  Button,
  MaterialPalette,
  PasswordValidator,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';

import ConfirmationView from './ConfirmationView';
import ErrorView from './ErrorView';

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    border: 0,
    borderRadius: 3,
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    color: MaterialPalette.common.white,
    height: 48,
    padding: '0 30px',
  },
  box: {
    height: '100vh',
    display: 'flex',
    minHeight: '600px',
    alignItems: 'center',
    zIndex: 2,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '0px 20px',
  },
  logo: {
    marginBottom: '50px',
  },
  linkContainer: {
    marginTop: '0px',
  },
  title: {
    textAlign: 'center',
    width: '100%',
  },
  button: {
    width: '190px',
    margin: '0 auto',
    marginTop: '50px',
    justifyContent: 'flex-start',
  },
  icon: { color: MaterialPalette.primary.main },
  passwordValidatorContainer: { padding: '4px', width: '100%' },
});

interface Props extends RouteComponentProps {
  user?: boolean;
}

interface State {
  password: string;
  validPassword: boolean;
  showPassword: boolean;
  passwordError: string | null;
  confirmPassword: string;
  validConfirmPassword: boolean;
  confirmPasswordError: string | null;
  username?: string;
  code: string;
  cognitoUser?: boolean;
  newPasswordRequired: boolean;
}

const ResetPassword = (props: Props) => {
  const classes = useStyles();
  const [requestedReset, setRequestedReset] = useState(false);
  const [resetError, setResetError] = useState(false);
  const [values, setValues] = useState<State>({
    password: '',
    validPassword: false,
    showPassword: false,
    passwordError: null,
    confirmPassword: '',
    validConfirmPassword: false,
    confirmPasswordError: null,
    username: '',
    code: '',
    cognitoUser: undefined,
    newPasswordRequired: false,
  });

  //Parse code from url
  useEffect(() => {
    const urlParams = new URLSearchParams(props.location.search);
    const confirmationCode = urlParams.get('confirmationCode');
    const username = urlParams.get('username');
    const { user } = props;
    if ((!confirmationCode || !username) && !user) return props.history.push('/login');
    setValues({
      ...values,
      code: confirmationCode || '',
      username: username ? atob(username) : undefined,
      cognitoUser: user,
      newPasswordRequired: user !== undefined,
    });
  }, []);

  const handleChange = (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      [prop]: event.target.value,
    });
  };

  const validateForm = () => {
    let passwordError = null;
    let confirmPasswordError = null;
    setValues({ ...values, passwordError, confirmPasswordError });

    // Make sure password is valid
    if (values.password.length < 1) {
      passwordError = 'Required.';
    } else if (!values.validPassword) {
      passwordError = 'Does not meet requirement';
    }

    // Make sure password confirm matches
    if (values.confirmPassword.length < 1) {
      confirmPasswordError = 'Required.';
    } else if (values.confirmPassword !== values.password) {
      confirmPasswordError = 'Passwords do not match';
    }

    setValues({ ...values, passwordError, confirmPasswordError });

    return passwordError === null && confirmPasswordError === null;
  };

  const handleForgotPasswordSubmit = async () => {
    const valid = validateForm();
    if (!valid) return;
    try {
      if (values.newPasswordRequired) {
        await completeNewPassword(values.cognitoUser, values.password);
      } else if (values.username) {
        await submitPasswordReset(values.username, values.code, values.password);
      }
      setRequestedReset(true);
    } catch (error) {
      setResetError(true);
    }
  };

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validPasswordHandler = (validPassword: boolean) => {
    setValues({ ...values, validPassword });
  };

  return (
    <>
      <Box className={classes.box}>
        <Container className={classes.container} maxWidth="sm">
          {/* <img src={Logo} />- */}
          {resetError && <ErrorView />}
          {requestedReset && !resetError && <ConfirmationView />}
          {!resetError && !requestedReset && (
            <Grid container spacing={0} className={classes.linkContainer} justifyContent="center">
              <Typography variant="h5" className={classes.title} fontWeight="bold">
                Set New Password
              </Typography>
              <TextField
                id="Password"
                key="Password"
                name="Password"
                label="Password"
                required
                type={values.showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={handleChange('password')}
                size="medium"
                errorText={values.passwordError !== null ? values.passwordError : ''}
                error={values.passwordError !== null}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {values.showPassword ? (
                        <Visibility className={classes.icon} />
                      ) : (
                        <VisibilityOff className={classes.icon} />
                      )}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <TextField
                required
                id="confirmPassword"
                key="confirmPassword"
                name="confirmPassword"
                label="Confirm New Password"
                type="password"
                onChange={handleChange('confirmPassword')}
                size="medium"
                errorText={values.confirmPasswordError !== null ? values.confirmPasswordError : ''}
                error={values.confirmPasswordError !== null}
              />
              <Grid item className={classes.passwordValidatorContainer}>
                <PasswordValidator
                  password={values.password}
                  isPasswordValid={validPasswordHandler}
                />
              </Grid>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                className={classes.button}
                fullWidth={false}
                noMinHeight
                onClick={handleForgotPasswordSubmit}
              >
                Set Password
              </Button>
            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
};

export default withRouter(ResetPassword);
